import { formatCount, formatRatio, VALOR_AUSENTE } from '../lib/format';

/**
 * Funil de conversao (RNF15).
 *
 * Escolhas de visualizacao, na ordem em que foram tomadas:
 *
 * 1. FORMA. As quatro etapas sao a MESMA medida (contagem de leads) encolhendo
 *    ao longo do processo, nao quatro categorias diferentes. Comparacao de
 *    magnitude pede barra; o trapezio decorativo de "funil" distorce area e faz
 *    o leitor comparar larguras que nao sao proporcionais ao valor.
 *
 * 2. COR. Medida unica = escala sequencial: uma matiz so, mais escuro para
 *    mais. Pintar cada etapa de uma cor de serie afirmaria que sao categorias
 *    independentes -- e ainda gastaria quatro slots da paleta categorica em algo
 *    que nao precisa de nenhum. Sem legenda de proposito: com uma serie so, o
 *    titulo ja diz o que esta plotado, e uma caixinha com um quadrado repete a
 *    informacao e ocupa espaco.
 *
 * 3. ROTULO. Valor e proporcao ficam acima da barra, na mesma linha do nome da
 *    etapa. Rotulo dentro da barra seria cortado na etapa mais estreita (a que
 *    mais importa: a de convertidas) e `overflow: hidden` para "resolver" isso
 *    comeria os primeiros caracteres.
 *
 * 4. SEM TOOLTIP. Todo valor ja esta escrito na tela e a tabela equivalente
 *    esta a um clique. Camada de hover aqui so acrescentaria um caminho que o
 *    teclado e o toque precisariam replicar sem ganhar nenhum dado novo.
 *
 * Feito com CSS puro: quatro barras nao justificam SVG, e largura em
 * porcentagem acompanha o container sem viewBox nem recalculo em JavaScript.
 */

export interface FunnelStageView {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Explicacao curta da etapa, exibida abaixo do nome. */
  readonly hint: string;
}

interface FunnelChartProps {
  readonly title: string;
  readonly description: string;
  readonly stages: readonly FunnelStageView[];
}

/*
 * Rampa sequencial de uma matiz so, do mais escuro (maior) ao mais claro
 * (menor). As classes sao literais completas porque o Tailwind varre o codigo
 * como texto: `bg-series-1/${n}` montado em runtime nunca seria gerado no CSS.
 */
const RAMPA: readonly string[] = [
  'bg-series-1',
  'bg-series-1/80',
  'bg-series-1/60',
  'bg-series-1/45',
];

const RAMPA_MINIMA = 'bg-series-1/30';

export function FunnelChart({ title, description, stages }: FunnelChartProps): JSX.Element {
  const primeira = stages[0];
  // A base do funil e a primeira etapa, nao o maior valor: se uma etapa
  // posterior vier maior por inconsistencia de dado, a barra satura em 100% e o
  // numero escrito denuncia o problema, em vez de a escala inteira se rebasear
  // silenciosamente.
  const base = primeira === undefined ? 0 : primeira.value;

  return (
    <figure className="flex flex-col gap-5 rounded-xl border border-line bg-surface px-5 py-5">
      <figcaption className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        <p className="max-w-prose text-sm text-ink-muted">{description}</p>
      </figcaption>

      {base <= 0 ? (
        <p className="text-sm text-ink-subtle">
          Nenhum carrinho abandonado no periodo: o funil comeca vazio.
        </p>
      ) : (
        <ol className="flex flex-col gap-4">
          {stages.map((stage, index) => (
            <FunnelRow
              key={stage.key}
              stage={stage}
              anterior={index === 0 ? null : (stages[index - 1] ?? null)}
              base={base}
              tom={RAMPA[index] ?? RAMPA_MINIMA}
            />
          ))}
        </ol>
      )}

      <FunnelTable stages={stages} base={base} />
    </figure>
  );
}

interface FunnelRowProps {
  readonly stage: FunnelStageView;
  readonly anterior: FunnelStageView | null;
  readonly base: number;
  readonly tom: string;
}

function FunnelRow({ stage, anterior, base, tom }: FunnelRowProps): JSX.Element {
  const proporcao = Math.min(stage.value / base, 1);

  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-sm font-medium text-ink">{stage.label}</span>
        <span className="text-sm text-ink-muted">
          {/* tabular-nums: valores empilhados alinham pela casa decimal. */}
          <span className="tabular-nums font-medium text-ink">{formatCount(stage.value)}</span>
          <span className="tabular-nums"> · {formatRatio(stage.value, base)} do total</span>
        </span>
      </div>

      {/*
        A trilha vazia e um passo claro da MESMA rampa, nao um cinza qualquer:
        assim a barra se le como preenchimento de um total, e nao como duas
        informacoes diferentes. Canto arredondado so na ponta do dado; o lado da
        linha de base fica reto, porque e de onde a barra cresce.
      */}
      <div className="h-4 w-full overflow-hidden rounded-sm bg-series-1/10">
        <div
          className={`h-full rounded-r ${tom}`}
          style={{ width: `${(proporcao * 100).toFixed(2)}%` }}
          aria-hidden="true"
        />
      </div>

      <p className="text-xs text-ink-subtle">
        {stage.hint}
        {anterior === null ? null : (
          <>
            {' · '}
            <span className="tabular-nums">
              {formatRatio(stage.value, anterior.value)}
            </span>{' '}
            da etapa anterior
          </>
        )}
      </p>
    </li>
  );
}

/**
 * Leitura alternativa do mesmo dado.
 *
 * Grafico nenhum e a unica porta de entrada: quem usa leitor de tela, quem
 * precisa do numero exato e quem vai copiar para uma planilha merecem a tabela.
 * Fica recolhida para nao competir com o grafico, mas sempre presente.
 */
function FunnelTable({
  stages,
  base,
}: {
  readonly stages: readonly FunnelStageView[];
  readonly base: number;
}): JSX.Element {
  return (
    <details>
      <summary className="cursor-pointer text-sm font-medium text-ink-muted transition-colors hover:text-ink">
        Ver os numeros do funil em tabela
      </summary>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[22rem] border-collapse text-sm">
          <caption className="sr-only">Etapas do funil de conversao, com contagem e proporcao</caption>
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
              <th scope="col" className="py-2 pr-4 font-medium">Etapa</th>
              <th scope="col" data-numeric="true" className="py-2 pr-4 text-right font-medium">Leads</th>
              <th scope="col" data-numeric="true" className="py-2 text-right font-medium">Do total</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => (
              <tr key={stage.key} className="border-b border-line/60 last:border-0">
                <th scope="row" className="py-2 pr-4 text-left font-normal text-ink">
                  {stage.label}
                </th>
                <td data-numeric="true" className="py-2 pr-4 text-right text-ink">
                  {formatCount(stage.value)}
                </td>
                <td data-numeric="true" className="py-2 text-right text-ink-muted">
                  {base > 0 ? formatRatio(stage.value, base) : VALOR_AUSENTE}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
